import { useState, useCallback, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/authStore';
import {
  aiService,
  type SuggestionsResponse,
  type SentimentAnalysisResponse,
  type QuickAnalyzeResponse,
  type TicketContext,
  type MessageContext,
  type CustomerContext,
  type ResponseTone,
} from '../services/ai.service';

// ==================== Query Keys ====================

/**
 * Query-key factory for the AI hooks. Mirrors the analyticsKeys pattern in
 * useAnalytics.ts. Keys are tenant-scoped so cached AI output never leaks
 * across tenants, and `lastMessageKey` is a STABLE digest of the last message
 * (id or content) — NOT `JSON.stringify(messages)` off a fresh array, which
 * never stabilises and self-perpetuates the fetch loop.
 */
export const aiKeys = {
  all: ['ai'] as const,
  suggestions: (
    tenantId: string,
    ticketId: string,
    lastMessageKey: string,
    tone?: ResponseTone,
  ) =>
    [...aiKeys.all, 'suggestions', tenantId, ticketId, lastMessageKey, tone ?? 'default'] as const,
  ticketAnalysis: (tenantId: string, ticketId: string) =>
    [...aiKeys.all, 'ticketAnalysis', tenantId, ticketId] as const,
};

const AI_STALE_TIME = 5 * 60 * 1000; // 5 minutes

/**
 * Build a stable, cheap digest of the LAST message in the conversation so the
 * query key only changes when a genuinely new message arrives. A new-but-equal
 * `messages` array (e.g. a fresh array from a Zustand store mutation) produces
 * the SAME key and therefore does NOT trigger a refetch.
 */
function lastMessageKeyOf(messages: MessageContext[]): string {
  if (!messages.length) return 'none';
  const last = messages[messages.length - 1];
  // Content + senderType + count is enough to distinguish a newly appended
  // message without serialising the whole array on every render.
  const content = last.content || '';
  const snippet = content.length > 64 ? content.slice(0, 64) : content;
  return `${messages.length}:${last.senderType}:${snippet}`;
}

export interface UseAISuggestionsOptions {
  ticket: TicketContext;
  messages: MessageContext[];
  customer?: CustomerContext;
  autoFetch?: boolean;
  debounceMs?: number;
}

export interface UseAISuggestionsResult {
  suggestions: SuggestionsResponse | null;
  isLoading: boolean;
  error: string | null;
  selectedTone: ResponseTone | undefined;
  fetchSuggestions: (tone?: ResponseTone) => Promise<void>;
  refresh: () => Promise<void>;
}

export function useAISuggestions({
  ticket,
  messages,
  customer,
  autoFetch = true,
  // Retained for API compatibility; React Query's staleTime/dedup now provides
  // the throttling the manual debounce used to.
  debounceMs: _debounceMs = 500,
}: UseAISuggestionsOptions): UseAISuggestionsResult {
  const queryClient = useQueryClient();
  const tenantId = useAuthStore((s) => s.user?.tenantId ?? '');
  const [selectedTone, setSelectedTone] = useState<ResponseTone | undefined>();

  const ticketId = ticket?.ticketId ?? '';
  const lastMessageKey = lastMessageKeyOf(messages);

  // Keep the latest inputs in a ref so the queryFn closure always sends the
  // current ticket/messages/customer without being part of the query key
  // (the key is intentionally minimal + stable to control refetch cadence).
  const inputsRef = useRef({ ticket, messages, customer });
  inputsRef.current = { ticket, messages, customer };

  const queryKey = aiKeys.suggestions(tenantId, ticketId, lastMessageKey, selectedTone);

  const query = useQuery({
    queryKey,
    queryFn: async (): Promise<SuggestionsResponse> => {
      const { ticket: t, messages: m, customer: c } = inputsRef.current;
      return aiService.generateSuggestions({
        ticket: t,
        messages: m,
        customer: c,
        count: 3,
        tone: selectedTone,
        includeArticles: true,
      });
    },
    enabled: autoFetch && !!ticketId && messages.length > 0,
    staleTime: AI_STALE_TIME,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // Manual trigger preserved for ResponseSuggestions' onRefresh(tone). Changing
  // the tone changes the query key; if that key is already cached/fetching,
  // invalidate so a fresh result is produced for the new tone.
  const fetchSuggestions = useCallback(
    async (tone?: ResponseTone) => {
      if (tone && tone !== selectedTone) {
        setSelectedTone(tone);
        return; // key change drives the (enabled) query automatically
      }
      const key = aiKeys.suggestions(tenantId, ticketId, lastMessageKey, tone ?? selectedTone);
      await queryClient.invalidateQueries({ queryKey: key });
    },
    [queryClient, tenantId, ticketId, lastMessageKey, selectedTone],
  );

  const refresh = useCallback(async () => {
    await query.refetch();
  }, [query]);

  return {
    suggestions: query.data ?? null,
    isLoading: query.isFetching,
    error: query.isError ? 'Failed to generate suggestions' : null,
    selectedTone,
    fetchSuggestions,
    refresh,
  };
}

// NOTE: useSentimentAnalysis / useQuickAnalyze / useMessagesSentiment below
// retain the manual useState/useEffect pattern. They are NOT currently mounted
// anywhere in the app and each guards its own fetch with a stable ref
// (lastTextRef / lastMessageRef / sentimentMap dedupe), so they do not exhibit
// the self-perpetuating refetch loop that useAISuggestions had. Migrating them
// to useQuery is deferred to avoid expanding scope; do so when they gain a
// consumer, keyed via aiKeys for consistency.

// Hook for sentiment analysis
export interface UseSentimentAnalysisOptions {
  text: string;
  includeAspects?: boolean;
  autoAnalyze?: boolean;
  debounceMs?: number;
}

export interface UseSentimentAnalysisResult {
  sentiment: SentimentAnalysisResponse | null;
  isLoading: boolean;
  error: string | null;
  analyze: () => Promise<void>;
}

export function useSentimentAnalysis({
  text,
  includeAspects = false,
  autoAnalyze = true,
  debounceMs = 300,
}: UseSentimentAnalysisOptions): UseSentimentAnalysisResult {
  const [sentiment, setSentiment] = useState<SentimentAnalysisResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<NodeJS.Timeout>();
  const lastTextRef = useRef<string>('');

  const analyze = useCallback(async () => {
    if (!text || text.trim().length === 0) {
      setSentiment(null);
      return;
    }

    if (text === lastTextRef.current && sentiment) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await aiService.analyzeSentiment(text, { includeAspects });
      setSentiment(result);
      lastTextRef.current = text;
    } catch (err) {
      console.error('Failed to analyze sentiment:', err);
      setError('Failed to analyze sentiment');
      setSentiment(null);
    } finally {
      setIsLoading(false);
    }
  }, [text, includeAspects, sentiment]);

  useEffect(() => {
    if (!autoAnalyze || !text) return;

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      analyze();
    }, debounceMs);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [autoAnalyze, text, debounceMs, analyze]);

  return {
    sentiment,
    isLoading,
    error,
    analyze,
  };
}

// Hook for quick analysis (combined intent + sentiment)
export interface UseQuickAnalyzeOptions {
  message: string;
  ticketId?: string;
  autoAnalyze?: boolean;
  debounceMs?: number;
}

export interface UseQuickAnalyzeResult {
  analysis: QuickAnalyzeResponse | null;
  isLoading: boolean;
  error: string | null;
  analyze: () => Promise<void>;
}

export function useQuickAnalyze({
  message,
  ticketId,
  autoAnalyze = true,
  debounceMs = 300,
}: UseQuickAnalyzeOptions): UseQuickAnalyzeResult {
  const [analysis, setAnalysis] = useState<QuickAnalyzeResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<NodeJS.Timeout>();
  const lastMessageRef = useRef<string>('');

  const analyze = useCallback(async () => {
    if (!message || message.trim().length === 0) {
      setAnalysis(null);
      return;
    }

    if (message === lastMessageRef.current && analysis) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await aiService.quickAnalyze(message, ticketId);
      setAnalysis(result);
      lastMessageRef.current = message;
    } catch (err) {
      console.error('Failed to analyze message:', err);
      setError('Failed to analyze message');
      setAnalysis(null);
    } finally {
      setIsLoading(false);
    }
  }, [message, ticketId, analysis]);

  useEffect(() => {
    if (!autoAnalyze || !message) return;

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      analyze();
    }, debounceMs);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [autoAnalyze, message, debounceMs, analyze]);

  return {
    analysis,
    isLoading,
    error,
    analyze,
  };
}

// Hook for batch sentiment analysis of messages
export function useMessagesSentiment(
  messages: Array<{ id: string; content: string }>
): Map<string, SentimentAnalysisResponse> {
  const [sentimentMap, setSentimentMap] = useState<Map<string, SentimentAnalysisResponse>>(
    new Map()
  );
  const analysisQueueRef = useRef<Array<{ id: string; text: string }>>([]);
  const isProcessingRef = useRef(false);

  const processQueue = useCallback(async () => {
    if (isProcessingRef.current || analysisQueueRef.current.length === 0) {
      return;
    }

    isProcessingRef.current = true;

    try {
      const batch = analysisQueueRef.current.splice(0, 10); // Process 10 at a time
      const results = await aiService.analyzeSentimentBatch(batch);

      setSentimentMap((prev) => {
        const newMap = new Map(prev);
        results.forEach(({ id, sentiment }) => {
          newMap.set(id, sentiment);
        });
        return newMap;
      });
    } catch (err) {
      console.error('Failed to analyze batch sentiment:', err);
    } finally {
      isProcessingRef.current = false;
      // Process remaining items if any
      if (analysisQueueRef.current.length > 0) {
        processQueue();
      }
    }
  }, []);

  useEffect(() => {
    // Find messages that need analysis
    const toAnalyze = messages.filter(
      (msg) => !sentimentMap.has(msg.id) && msg.content.trim().length > 0
    );

    if (toAnalyze.length === 0) return;

    // Add to queue
    analysisQueueRef.current.push(
      ...toAnalyze.map((msg) => ({ id: msg.id, text: msg.content }))
    );

    // Start processing
    processQueue();
  }, [messages, sentimentMap, processQueue]);

  return sentimentMap;
}
